/**
 * BrowserView — a styled mini-browser WINDOW over the tenant's ONE shared headed
 * Chromium (browser-mcp, Xvfb + x11vnc), rendered as an interactive noVNC canvas.
 *
 * This is the SAME page the Pi/voice agents drive: when an agent navigates,
 * clicks or scrolls, the operator sees it live; the operator can also drive it
 * directly (real RFB pointer/keyboard input on the canvas).
 *
 * Chrome (2026-07-08 mini-browser pass): a real toolbar — back / forward /
 * reload / URL + Go — plus a maximize control that promotes the pane to a
 * full-window overlay, and a "raw" toggle that hides the chrome for a bare noVNC
 * debug view. The URL bar + nav buttons drive the shared page through the
 * agents' own browse tools (/api/browse/*); the canvas reflects the result live.
 *
 * ⚠ Mount discipline: the RFB session is a live WebSocket held for the lifetime
 * of THIS component instance. Maximize/restore and raw/chrome only toggle CSS
 * classes on the SAME root element — they never move the component in the tree,
 * so the socket is never torn down. rfb.scaleViewport refits the framebuffer to
 * the pane automatically (its own ResizeObserver).
 *
 * Transport (see docs/20260708/..._PLAN_HEADED_BROWSER_NOVNC_*.md):
 *   1. POST /api/browse/vnc-ticket (JWT via services/api) → single-use ~30s ticket
 *   2. RFB → wss?/ws same-origin /api/browse/vnc?ticket=… → vibe-backend proxy →
 *      browser-mcp /vnc (X-Browse-Token, server-side) → loopback x11vnc.
 * The target site is NOT iframed (X-Frame-Options/CSP forbid it, and it would
 * bypass the shared session + SSRF proxy) — noVNC mirrors the server-side display.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Globe,
  AlertTriangle,
  Loader2,
  Maximize2,
  Minimize2,
  PanelTopClose,
  PanelTopOpen,
} from 'lucide-react';
import RFB from '@novnc/novnc';
import { api, ApiError } from '@/services/api';

type Status = 'connecting' | 'connected' | 'disconnected' | 'error';

interface BrowseAPIResponse {
  content: string;
  isError: boolean;
}
interface VncTicketResponse {
  ticket: string;
  ttl_seconds: number;
}

export function BrowserView() {
  const screenRef = useRef<HTMLDivElement | null>(null);
  const rfbRef = useRef<RFB | null>(null);
  const [status, setStatus] = useState<Status>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [navBusy, setNavBusy] = useState(false);
  // Bumping this re-runs the connect effect (manual reconnect).
  const [connectNonce, setConnectNonce] = useState(0);
  // Window controls — CSS-only, never remount the RFB session (see header note).
  const [maximized, setMaximized] = useState(false);
  const [raw, setRaw] = useState(false);

  const disconnect = useCallback(() => {
    if (rfbRef.current) {
      try {
        rfbRef.current.disconnect();
      } catch {
        /* already gone */
      }
      rfbRef.current = null;
    }
  }, []);

  // Connect (or reconnect) the noVNC session to the shared headed browser.
  useEffect(() => {
    let cancelled = false;
    setStatus('connecting');
    setError(null);

    (async () => {
      let ticket: string;
      try {
        ticket = (await api.post<VncTicketResponse>('/browse/vnc-ticket', {})).ticket;
      } catch (e) {
        if (cancelled) return;
        const s = e instanceof ApiError ? e.status : 0;
        setError(
          s === 503
            ? 'Web browsing is not enabled for this tenant.'
            : `Could not start a browser session (${e instanceof ApiError ? e.status : 'network'}).`,
        );
        setStatus('error');
        return;
      }
      if (cancelled || !screenRef.current) return;

      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsUrl = `${proto}://${window.location.host}/api/browse/vnc?ticket=${encodeURIComponent(ticket)}`;
      try {
        const rfb = new RFB(screenRef.current, wsUrl, {});
        // v1.2.2: ask the server to resize its desktop to the pane (TigerVNC
        // honors this → the framebuffer follows the pane, no letterbox). The
        // ResizeObserver below resizes the browser WINDOW to match, so the page
        // fills. scaleViewport stays on as a fallback until the first resize.
        rfb.resizeSession = true;
        rfb.scaleViewport = true; // fit the framebuffer to the pane
        rfb.background = '#0b0b0b';
        rfb.addEventListener('connect', () => {
          if (!cancelled) setStatus('connected');
        });
        rfb.addEventListener('disconnect', (e: CustomEvent) => {
          if (cancelled) return;
          // e.detail.clean === false → unexpected drop.
          const clean = (e.detail as { clean?: boolean } | undefined)?.clean;
          setStatus('disconnected');
          if (clean === false) setError('The browser session was disconnected.');
        });
        rfbRef.current = rfb;
      } catch {
        if (!cancelled) {
          setError('Failed to initialize the browser view.');
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      disconnect();
    };
  }, [connectNonce, disconnect]);

  const reconnect = useCallback(() => {
    disconnect();
    setConnectNonce((n) => n + 1);
  }, [disconnect]);

  // v1.2.2: keep the shared browser WINDOW sized to this pane so the page fills
  // it (paired with rfb.resizeSession, which sizes the framebuffer). A
  // ResizeObserver on the canvas container fires on pane drags AND on
  // maximize/restore; debounced so a drag doesn't hammer CDP, and de-duped so an
  // identical size is never re-sent. Best-effort — a failed resize never
  // surfaces to the operator. Guarded for jsdom (no ResizeObserver).
  const resizeTimer = useRef<number | null>(null);
  const lastSentSize = useRef<string>('');
  useEffect(() => {
    const el = screenRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const sendResize = () => {
      const width = Math.round(el.clientWidth);
      const height = Math.round(el.clientHeight);
      if (width < 1 || height < 1) return;
      const key = `${width}x${height}`;
      if (key === lastSentSize.current) return;
      lastSentSize.current = key;
      void api.post('/browse/resize', { width, height }).catch(() => {
        /* best-effort; reset so a later identical size can retry */
        lastSentSize.current = '';
      });
    };
    const ro = new ResizeObserver(() => {
      if (resizeTimer.current) window.clearTimeout(resizeTimer.current);
      resizeTimer.current = window.setTimeout(sendResize, 250);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (resizeTimer.current) window.clearTimeout(resizeTimer.current);
    };
  }, []);

  // Reflect the shared page's REAL current URL in the address bar. The browse
  // tools return a JSON string ({url,title,...}); parse it best-effort. Never
  // clobber the bar with an error string.
  const reflectUrl = useCallback((content: string) => {
    try {
      const parsed = JSON.parse(content) as { url?: unknown };
      if (typeof parsed.url === 'string') setUrlInput(parsed.url);
    } catch {
      /* content was an error/plain string — leave the bar as-is */
    }
  }, []);

  // Drive the SAME shared page (the agents' navigate tool). The canvas updates
  // live via noVNC — no client-side page load.
  const navigate = useCallback(
    async (target: string) => {
      const t = target.trim();
      if (!t) return;
      const full = /^https?:\/\//i.test(t) ? t : `https://${t}`;
      setNavBusy(true);
      setError(null);
      try {
        const r = await api.post<BrowseAPIResponse>('/browse/navigate', { url: full });
        if (r.isError) setError(r.content || 'Navigation failed.');
        else reflectUrl(r.content);
      } catch (e) {
        const s = e instanceof ApiError ? e.status : 0;
        setError(s === 503 ? 'Web browsing is not enabled for this tenant.' : 'Navigation failed.');
      } finally {
        setNavBusy(false);
      }
    },
    [reflectUrl],
  );

  // Back / Forward / Reload — the toolbar history controls. Each drives the
  // shared page server-side and reflects the resulting URL.
  const runHistory = useCallback(
    async (action: 'back' | 'forward' | 'reload') => {
      setNavBusy(true);
      try {
        const r = await api.post<BrowseAPIResponse>(`/browse/${action}`, {});
        if (!r.isError) reflectUrl(r.content);
      } catch {
        /* best-effort */
      } finally {
        setNavBusy(false);
      }
    },
    [reflectUrl],
  );

  const rootCls = maximized
    ? 'fixed inset-0 z-50 flex flex-col bg-vibe-bg overflow-hidden shadow-2xl'
    : 'flex flex-col h-full w-full overflow-hidden';

  const navDisabled = navBusy || status !== 'connected';

  return (
    <div className={rootCls} data-testid="browser-view" data-maximized={maximized}>
      {/* Toolbar / window title bar — hidden in raw mode. */}
      {!raw && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-vibe-border bg-vibe-bg-secondary">
          <button
            type="button"
            onClick={() => void runHistory('back')}
            disabled={navDisabled}
            title="Back"
            aria-label="Back"
            data-testid="browser-back"
            className="p-1.5 rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-40"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => void runHistory('forward')}
            disabled={navDisabled}
            title="Forward"
            aria-label="Forward"
            data-testid="browser-forward"
            className="p-1.5 rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-40"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => void runHistory('reload')}
            disabled={navDisabled}
            title="Reload"
            aria-label="Reload"
            data-testid="browser-reload"
            className="p-1.5 rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${navBusy ? 'animate-spin' : ''}`} />
          </button>
          <div className="flex items-center flex-1 min-w-0 gap-1.5 px-2 py-1 rounded bg-vibe-bg-tertiary">
            <Globe className="w-3.5 h-3.5 text-vibe-text-secondary flex-shrink-0" />
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void navigate(urlInput);
              }}
              placeholder="Enter a URL and press Enter (drives the shared browser)"
              data-testid="browser-url-input"
              spellCheck={false}
              className="flex-1 min-w-0 bg-transparent text-vibe-text text-xs outline-none border-none"
            />
          </div>
          <button
            type="button"
            onClick={() => void navigate(urlInput)}
            disabled={navBusy || !urlInput.trim()}
            data-testid="browser-go-button"
            className="px-2.5 py-1 rounded bg-vibe-accent/10 hover:bg-vibe-accent/20 text-vibe-accent text-xs font-medium disabled:opacity-40"
          >
            Go
          </button>
          <span
            data-testid="browser-conn-status"
            className="flex items-center gap-1 text-xs pl-1 flex-shrink-0"
            title={`VNC ${status}`}
          >
            <span
              className={
                status === 'connected'
                  ? 'w-2 h-2 rounded-full bg-green-500'
                  : status === 'connecting'
                    ? 'w-2 h-2 rounded-full bg-yellow-500'
                    : 'w-2 h-2 rounded-full bg-red-500'
              }
            />
            <span className="text-vibe-text-secondary">{status}</span>
          </span>
          {/* Raw (bare noVNC) toggle — hides the chrome for a debug view. */}
          <button
            type="button"
            onClick={() => setRaw(true)}
            title="Hide toolbar (raw noVNC)"
            aria-label="Hide toolbar (raw noVNC)"
            data-testid="browser-raw-toggle"
            className="p-1.5 rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary flex-shrink-0"
          >
            <PanelTopClose className="w-4 h-4" />
          </button>
          {/* Maximize / restore the window. */}
          <button
            type="button"
            onClick={() => setMaximized((m) => !m)}
            title={maximized ? 'Restore' : 'Maximize'}
            aria-label={maximized ? 'Restore' : 'Maximize'}
            data-testid="browser-maximize"
            className="p-1.5 rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary flex-shrink-0"
          >
            {maximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      )}

      {/* Error banner */}
      {error && !raw && (
        <div
          data-testid="browser-error"
          className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border-b border-red-500/30 text-xs text-red-400"
        >
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{error}</span>
        </div>
      )}

      {/* Live VNC canvas (noVNC injects a <canvas> into this container) */}
      <div className="relative flex-1 min-h-0 bg-[#0b0b0b]">
        <div ref={screenRef} data-testid="browser-vnc-screen" className="absolute inset-0" />
        {/* In raw mode the only chrome is a small restore-toolbar button. */}
        {raw && (
          <button
            type="button"
            onClick={() => setRaw(false)}
            title="Show toolbar"
            aria-label="Show toolbar"
            data-testid="browser-chrome-toggle"
            className="absolute top-2 right-2 z-10 p-1.5 rounded bg-vibe-bg-secondary/80 text-vibe-text-secondary hover:bg-vibe-bg-tertiary"
          >
            <PanelTopOpen className="w-4 h-4" />
          </button>
        )}
        {status === 'connecting' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-vibe-text-secondary pointer-events-none">
            <Loader2 className="w-8 h-8 animate-spin text-vibe-accent mb-2" />
            <div className="text-sm">Connecting to the browser…</div>
          </div>
        )}
        {(status === 'disconnected' || status === 'error') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-vibe-text-secondary">
            <Globe className="w-12 h-12 opacity-40 mb-2" />
            <div className="text-sm mb-3">
              {status === 'error' ? 'Browser session unavailable' : 'Disconnected'}
            </div>
            <button
              type="button"
              onClick={reconnect}
              data-testid="browser-reconnect"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-vibe-accent/10 hover:bg-vibe-accent/20 text-vibe-accent text-xs font-medium"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Reconnect
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
